// apps/client/src/hooks/useCustomerPortfolio.ts
import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export interface CustomerPortfolioFilters {
  companyId: string;
  officeId?: string;
  brokerId?: string;
  brokerName?: string;  // Filter by broker display name
  search?: string;      // Customer name search
  templateId?: string;  // Loan book filter ('all' or specific templateId)
  limit?: number;
  entityCode?: string;
}

export interface LoanDetail {
  loanId: string;
  loanAmountCents: number;
  brokerName: string | null;
  officeId: string | null;
  lender: string | null;
  settlementDate: string | null;  // ISO date string
}

export interface CustomerRecord {
  customerName: string;
  loanCount: number;
  loanIds: string[];
  loans: LoanDetail[];  // Includes loan amounts
  totalLoanValueCents: number;
  lifetimeCommissionCents: number;
  upfrontCommissionCents: number;
  trailCommissionCents: number;
}

export interface CustomerPortfolioResponse {
  customers: CustomerRecord[];
  summary: {
    totalCustomers: number;
    totalLoans: number;
    totalCommissionCents: number;
    totalUpfrontCommissionCents: number;
    totalTrailCommissionCents: number;
    averageAgeYears: number;  // Average portfolio age from earliest settlement dates
  };
}

interface UseCustomerPortfolioReturn {
  data: CustomerPortfolioResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook to fetch customer portfolio analytics
 * Used for: Customer Portfolio View (customers with multiple loans)
 *
 * BUSINESS VALUE:
 * - Identify high-value customers (19% have 2+ loans)
 * - Track customer lifetime value
 * - Target for upsell/cross-sell opportunities
 *
 * RBAC:
 * - broker: See own customers only
 * - office_admin: See office customers only
 * - administrator/super_admin: See all customers
 */
export function useCustomerPortfolio(filters: CustomerPortfolioFilters): UseCustomerPortfolioReturn {
  const [data, setData] = useState<CustomerPortfolioResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const refetch = () => {
    setRefetchTrigger((prev) => prev + 1);
  };

  useEffect(() => {
    if (!filters.companyId) {
      setLoading(false);
      return;
    }

    const fetchCustomerPortfolio = async () => {
      setLoading(true);
      setError(null);

      try {
        console.log('[useCustomerPortfolio] Fetching customer portfolio:', filters);

        const getCustomerPortfolioFunction = httpsCallable(functions, 'getCustomerPortfolio');
        const result = await getCustomerPortfolioFunction(filters);

        const response = result.data as CustomerPortfolioResponse;

        console.log('[useCustomerPortfolio] Received data:', {
          totalCustomers: response.summary.totalCustomers,
          totalLoans: response.summary.totalLoans,
          totalCommission: response.summary.totalCommissionCents / 100,
        });

        setData(response);
      } catch (err: any) {
        console.error('[useCustomerPortfolio] Error:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch customer portfolio');
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerPortfolio();
  }, [filters.companyId, filters.officeId, filters.brokerId, filters.brokerName, filters.search, filters.templateId, filters.limit, filters.entityCode, refetchTrigger]);

  return {
    data,
    loading,
    error,
    refetch,
  };
}
